import { Formik, useFormikContext } from 'formik'
import { Box, InputAdornment, makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import React from 'react'
import * as Yup from 'yup'

import { formatDate, generateOrderNumber } from 'src/utils'

import {
  Chip,
  Container,
  FIELD_REQUIRED,
  FormikSelect,
  FormikTextField,
  FormikToggle,
  OrganisationName,
  PageTitle,
  SidePanel,
  Typography,
} from 'src/modules/ui'

const useStyles = makeStyles(theme => ({
  body: {
    backgroundColor: theme.palette.fadedPurple.main,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormControl-root': {
      width: 'auto',
      [theme.breakpoints.up('laptop')]: {
        width: '100%',
      },
    },
    '& .MuiFormControl-fullWidth': {
      width: '100%',
    },
    '& .MuiFormControl-marginNormal': {
      marginBottom: 2,
      marginRight: 2,
      marginTop: 0,
      [theme.breakpoints.up('laptop')]: {
        marginTop: theme.spacing(1),
      },
    },
    '& .MuiFilledInput-root': {
      backgroundColor: 'white',
      border: `1px solid ${theme.palette.fadedPurple.main}`,
      '&.Mui-focused': {
        borderColor: theme.palette.lightPurple.main,
      },
      '&:hover': {
        borderColor: theme.palette.lightPurple.main,
      },
    },
    [theme.breakpoints.up('desktop')]: {
      flexDirection: 'row',
    },
  },
  chip: {
    marginLeft: theme.spacing(2),
  },
  content: {
    flexGrow: 1,
  },
  orderAgain: {
    marginBottom: theme.spacing(2),
  },
  productsHeader: {
    fontFamily: 'Gotham',
    fontWeight: 500,
    textTransform: 'uppercase',
    marginBottom: theme.spacing(1),
  },
  productTable: {
    borderRadius: theme.shape.borderRadius,
    width: '100%',
  },
  sidePanelContainer: {
    flexShrink: 0,
    marginLeft: 0,
    marginBottom: theme.spacing(4),
    width: 'auto',
    [theme.breakpoints.up('desktop')]: {
      marginBottom: 0,
      marginLeft: theme.spacing(4),
      width: 250,
    },
  },
  smallInput: {
    '& .MuiFormControl-root': { width: 75 },
  },
  midInput: {
    '& .MuiFormControl-root': { width: 105 },
  },
  table: {
    borderSpacing: 0,
    '& td': {
      paddingBottom: theme.spacing(2),
    },
    width: '100%',
  },
  rowInner: {
    display: 'flex',
    flexWrap: 'wrap',
    [theme.breakpoints.up('laptop')]: {
      flexWrap: 'nowrap',
    },
  },
}))

const EditOrder = order => {
  const {
    addressName,
    addressLine1,
    addressLine2,
    addressCity,
    addressCounty,
    addressPostcode,
    createdAt,
    lineItems,
    notes = '',
    organisation,
    sidePanelItems,
    status,
    statusChangedAt,
    noteOnly,
    scheduleLater,
  } = order
  const classes = useStyles()

  const products = lineItems
    .map(lineItem => ({
      available: true,
      id: lineItem.id,
      avEquivalentUnitSize: lineItem.productAvEquivalentUnitSize,
      code: lineItem.productCode,
      excludeFromOrder: lineItem.excludeFromOrder,
      name: lineItem.updatedProductName || lineItem.productName,
      price: lineItem.updatedProductPrice || lineItem.productPrice,
      unit: lineItem.updatedProductUnit || lineItem.productUnit,
      quantity: lineItem.updatedQuantity || lineItem.quantity,
      totalSizeSent: lineItem.totalSizeSent,
      variablePrice: lineItem.productVariablePrice,
      variablePriceUnit: lineItem.productVariablePriceUnit,
      vatRate: lineItem.productVatRate,
    }))
    .sort((a, b) => a.name.localeCompare(b.name))

  const orderNumber = generateOrderNumber({ ...order, supplier: organisation })

  const initialValues = products.reduce(
    (values, product) => {
      values[String(product.id)] = {
        includeInOrder: !product.excludeFromOrder,
        name: product.name,
        quantity: product.quantity,
        unit: product.unit,
        variablePrice: product.variablePrice,
        variablePriceUnit: product.variablePriceUnit || 'kg',
        price: product.price,
        totalSizeSent:
          product.totalSizeSent ||
          (product.variablePrice
            ? product.quantity * product.avEquivalentUnitSize
            : ''),
      }
      return values
    },
    { notes }
  )

  const reduceToLineItemValidation = (acc, option) => {
    return {
      ...acc,
      [option.id]: Yup.object().shape({
        name: Yup.string().required(FIELD_REQUIRED),
        quantity: Yup.number()
          .typeError('Invalid number')
          .moreThan(0)
          .required('Must provide a quantity'),
        unit: Yup.string().required(FIELD_REQUIRED),
        variablePrice: Yup.bool(),
        variablePriceUnit: Yup.string().when(`variablePrice`, {
          is: true,
          then: Yup.string().required(FIELD_REQUIRED),
        }),
        price: Yup.number()
          .typeError('Invalid number')
          .moreThan(0)
          .required('Must provide a price'),
        totalSizeSent: Yup.number().when(`variablePrice`, {
          is: true,
          then: Yup.number()
            .typeError('Invalid number')
            .moreThan(0)
            .required('Must provide a total weight'),
        }),
      }),
    }
  }

  const validationSchema = Yup.object().shape(
    products.reduce(reduceToLineItemValidation, {})
  )

  return (
    <Container>
      <PageTitle>
        Order {orderNumber}
        {status === 'cancelled' ? (
          <Chip label="Cancelled" className={classes.chip} />
        ) : status === 'dispatched' ? (
          <Chip
            label="Dispatched"
            className={classes.chip}
            color="lightGreen"
          />
        ) : null}
      </PageTitle>
      <OrganisationName {...organisation} />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={() => null}
      >
        <div className={classes.body}>
          <div className={classes.content}>
            {!noteOnly && (
              <Typography className={classes.productsHeader} color="lightGreen">
                Products
              </Typography>
            )}
            <table className={classes.table}>
              <tbody>
                {!noteOnly && (
                  <>
                    {products.map(({ id }) => (
                      <ProductRow id={id} key={id} />
                    ))}
                  </>
                )}
                <tr>
                  <td>
                    <FormikTextField
                      fullWidth
                      label="Notes"
                      name="notes"
                      margin="normal"
                      multiline
                      minRows={10}
                      type="text"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className={classes.sidePanelContainer}>
            <SidePanel>
              {status === 'dispatched' ? (
                <SidePanel.Item title="Dispatched On">
                  <Typography variant="body2">
                    {formatDate(statusChangedAt)}
                  </Typography>
                </SidePanel.Item>
              ) : status === 'cancelled' ? (
                <SidePanel.Item title="Cancelled On">
                  <Typography variant="body2">
                    {formatDate(statusChangedAt)}
                  </Typography>
                </SidePanel.Item>
              ) : null}
              <SidePanel.Item title="Date Submitted">
                <Typography variant="body2">{formatDate(createdAt)}</Typography>
              </SidePanel.Item>
              <SidePanel.Item title="Address">
                <Typography variant="body2">{addressName}</Typography>
                <Typography variant="body2">{addressLine1}</Typography>
                <Typography variant="body2">{addressLine2}</Typography>
                <Typography variant="body2">{addressCity}</Typography>
                <Typography variant="body2">{addressCounty}</Typography>
                <Typography variant="body2">{addressPostcode}</Typography>
              </SidePanel.Item>
              <SidePanel.Item title="Order Notes">
                <Typography variant="body2">{notes}</Typography>
              </SidePanel.Item>
              {scheduleLater ? (
                <SidePanel.Item title="Requested Delivery Date">
                  <Typography variant="body2">
                    {formatDate(scheduleLater)}
                  </Typography>
                </SidePanel.Item>
              ) : null}
              {sidePanelItems}
            </SidePanel>
          </div>
        </div>
      </Formik>
    </Container>
  )
}

const ProductRow = ({ id }) => {
  const classes = useStyles()
  const { values } = useFormikContext()
  const { includeInOrder, variablePrice, variablePriceUnit } = values[id]

  return (
    <tr key={`${includeInOrder}_${variablePriceUnit}_${variablePrice}`}>
      <td>
        <Box className={classes.rowInner}>
          <Box className={classes.smallInput}>
            <FormikToggle
              margin="normal"
              name={`${id}.includeInOrder`}
              label="Include"
            />
          </Box>
          <FormikTextField
            label="Name"
            name={`${id}.name`}
            margin="normal"
            placeholder="0"
            disabled={!includeInOrder}
          />
          <Box className={classes.smallInput}>
            <FormikTextField
              label="Quantity"
              name={`${id}.quantity`}
              margin="normal"
              placeholder="0"
              disabled={!includeInOrder}
            />
          </Box>
          <FormikTextField
            margin="normal"
            name={`${id}.unit`}
            label="Unit"
            disabled={!includeInOrder}
          />
          <Box className={classes.midInput}>
            <FormikToggle
              margin="normal"
              name={`${id}.variablePrice`}
              label="Variable Price"
              color="primary"
              disabled={!includeInOrder}
            />
          </Box>
          {variablePrice && (
            <Box className={classes.midInput}>
              <FormikSelect
                className={classes.select}
                margin="normal"
                name={`${id}.variablePriceUnit`}
                label="Pricing Unit"
                options={[{ value: 'kg', label: 'kg' }]}
                disabled={!includeInOrder}
              />
            </Box>
          )}
          <FormikTextField
            label={variablePrice ? `Price per ${variablePriceUnit}` : 'Price'}
            name={`${id}.price`}
            margin="normal"
            placeholder="0.00"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">£</InputAdornment>
              ),
              endAdornment: variablePrice && (
                <InputAdornment position="end">
                  {`/${variablePriceUnit}`}
                </InputAdornment>
              ),
            }}
            disabled={!includeInOrder}
          />
          {variablePrice && (
            <FormikTextField
              label="Total Weight"
              name={`${id}.totalSizeSent`}
              margin="normal"
              placeholder="0"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {variablePriceUnit}
                  </InputAdornment>
                ),
              }}
              disabled={!includeInOrder}
            />
          )}
        </Box>
      </td>
    </tr>
  )
}

EditOrder.propsTypes = {
  id: PropTypes.number,
  addressName: PropTypes.string,
  addressLine1: PropTypes.string,
  addressLine2: PropTypes.string,
  addressCity: PropTypes.string,
  addressCounty: PropTypes.string,
  addressPostcode: PropTypes.string,
  organisation: PropTypes.object,
  createdAt: PropTypes.string,
  lineItems: PropTypes.array,
  price: PropTypes.string,
  status: PropTypes.oneOf(['dispatched', 'cancelled', 'submitted']),
  statusChangedAt: PropTypes.string,
}

export default EditOrder
